import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useOptionalMessage } from 'Hooks/useOptionalMessage';
import { appConfigStore } from 'Models/AppConfigStore';
import If from 'Views/Components/If/If';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { TextInput } from 'Views/Components/New/TextInput';
import { PhoneInput } from 'Views/Components/New/PhoneInput';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { FreightBookingWizardData } from '../../BookingWizardData/FreightBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import { WizardErrors } from '../../BookingWizardTypes';
import DynamicFieldsSection from '../BookingDetails/DynamicFieldsSection';
import { useSubjectUserDisplayNameId } from '../BookingDetails/useSubjectUserDisplayNameId';

function FreightContactTab() {
	const intl = useIntl();
	const fmtOptional = useOptionalMessage();
	const subjectUserDisplayNameId = useSubjectUserDisplayNameId();
	const { wizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } = useBookingWizardDataStore();

	// TODO BW - how should we handle this situation?
	if (!(wizardData.innerWizardData instanceof FreightBookingWizardData)) {
		return <></>;
	}

	const typedErrors = innerWizardErrors as WizardErrors<FreightBookingWizardData>;
	const inner = wizardData.innerWizardData;

	const updateField = <K extends keyof FreightBookingWizardData>(field: K, value: FreightBookingWizardData[K]) => {
		const newData = new BookingWizardData({ ...wizardData });
		if (newData.innerWizardData instanceof FreightBookingWizardData) {
			(newData.innerWizardData as any)[field] = value;
		}
		setWizardData(newData);

		const newErrors = { ...typedErrors };
		delete (newErrors as any)[field];
		setInnerWizardErrors(newErrors);
	};

	const contactSubtitle = fmtOptional({
		id: 'freight-booking-wizard__shipment-contact-details__subtitle',
		defaultMessage: 'Tell us who to contact about this shipment.',
	});

	return (
		<div className="booking-wizard-tab shipment-details-tab">
			<WizardStepHeader
				iconName="contacts"
				title={intl.formatMessage({
					id: 'freight-booking-wizard__shipment-contact-details__header',
					defaultMessage: 'Contact details',
				})}
				subtitle={contactSubtitle || undefined}
				showCloseLink={wizardData.wizardMode === 'ALTERATION'}
				bookingToEditId={wizardData?.bookingToEdit ?? ''}
			/>
			<div className="flex flex-col gap-3">
				<If condition={appConfigStore.freightReceiverDetailsEnabled}>
					<section
						className="freight-contact-section booking-section-card"
						data-test-id="freight-receiver-details"
					>
						<header className="booking-section-header">
							<span className="booking-section-heading__icon">
								<Icon name="person" useMaterialIcons />
							</span>
							<div className="booking-section-header__text">
								<h4 className="booking-section-title">
									<FormattedMessage
										id="freight-booking-wizard__contact-tab__receiver-details__header"
										defaultMessage="Receiver details"
									/>
								</h4>
							</div>
						</header>
						<div className="freight-contact-body grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
							<If condition={appConfigStore.freightReceiverDetailsFirstNameEnabled}>
								<div data-test-id="freight-receiver-first-name-field">
									<TextInput
										id="freight-receiver-first-name"
										label={intl.formatMessage({
											id: 'freight-booking-wizard__contact-tab__receiver-details__first-name__label',
											defaultMessage: 'First name',
										})}
										placeholder={intl.formatMessage({
											id: 'freight-booking-wizard__contact-tab__receiver-details__first-name__placeholder',
											defaultMessage: 'Enter first name',
										})}
										required={appConfigStore.freightReceiverDetailsFirstNameRequired}
										value={inner.receiverFirstName ?? ''}
										error={typedErrors?.receiverFirstName}
										onChange={e => updateField('receiverFirstName', e.target.value)}
									/>
								</div>
							</If>
							<If condition={appConfigStore.freightReceiverDetailsLastNameEnabled}>
								<div data-test-id="freight-receiver-last-name-field">
									<TextInput
										id="freight-receiver-last-name"
										label={intl.formatMessage({
											id: 'freight-booking-wizard__contact-tab__receiver-details__last-name__label',
											defaultMessage: 'Last name',
										})}
										placeholder={intl.formatMessage({
											id: 'freight-booking-wizard__contact-tab__receiver-details__last-name__placeholder',
											defaultMessage: 'Enter last name',
										})}
										required={appConfigStore.freightReceiverDetailsLastNameRequired}
										value={inner.receiverLastName ?? ''}
										error={typedErrors?.receiverLastName}
										onChange={e => updateField('receiverLastName', e.target.value)}
									/>
								</div>
							</If>
							<If condition={appConfigStore.freightReceiverDetailsPhoneEnabled}>
								<div data-test-id="freight-receiver-phone-field">
									<PhoneInput
										id="freight-receiver-phone"
										label={intl.formatMessage({
											id: 'freight-booking-wizard__contact-tab__receiver-details__phone__label',
											defaultMessage: 'Phone',
										})}
										placeholder={intl.formatMessage({
											id: 'freight-booking-wizard__contact-tab__receiver-details__phone__placeholder',
											defaultMessage: 'Enter phone',
										})}
										required={appConfigStore.freightReceiverDetailsPhoneRequired}
										value={inner.receiverPhone ?? ''}
										error={typedErrors?.receiverPhone}
										onChange={value => updateField('receiverPhone', value)}
									/>
								</div>
							</If>
							<If condition={appConfigStore.freightReceiverDetailsEmailEnabled}>
								<div data-test-id="freight-receiver-email-field">
									<TextInput
										id="freight-receiver-email"
										label={intl.formatMessage({
											id: 'freight-booking-wizard__contact-tab__receiver-details__email__label',
											defaultMessage: 'Email',
										})}
										placeholder={intl.formatMessage({
											id: 'freight-booking-wizard__contact-tab__receiver-details__email__placeholder',
											defaultMessage: 'Enter email',
										})}
										required={appConfigStore.freightReceiverDetailsEmailRequired}
										value={inner.receiverEmail ?? ''}
										error={typedErrors?.receiverEmail}
										onChange={e => updateField('receiverEmail', e.target.value)}
									/>
								</div>
							</If>
							<If condition={appConfigStore.freightReceiverDetailsCompanyNameEnabled}>
								<div data-test-id="freight-receiver-company-name-field">
									<TextInput
										id="freight-receiver-company-name"
										label={intl.formatMessage({
											id: 'freight-booking-wizard__contact-tab__receiver-details__company-name__label',
											defaultMessage: 'Company name',
										})}
										placeholder={intl.formatMessage({
											id: 'freight-booking-wizard__contact-tab__receiver-details__company-name__placeholder',
											defaultMessage: 'Enter company name',
										})}
										required={appConfigStore.freightReceiverDetailsCompanyNameRequired}
										value={inner.receiverCompanyName ?? ''}
										error={typedErrors?.receiverCompanyName}
										onChange={e => updateField('receiverCompanyName', e.target.value)}
									/>
								</div>
							</If>
						</div>
					</section>
				</If>
				<If condition={appConfigStore.freightNotifyDetailsEnabled}>
					<section
						className="freight-contact-section booking-section-card"
						data-test-id="freight-notify-details"
					>
						<header className="booking-section-header">
							<span className="booking-section-heading__icon">
								<Icon name="notifications" useMaterialIcons />
							</span>
							<div className="booking-section-header__text">
								<h4 className="booking-section-title">
									<FormattedMessage
										id="freight-booking-wizard__contact-tab__notify-details__header"
										defaultMessage="Notify details"
									/>
								</h4>
							</div>
						</header>
						<div className="freight-contact-body grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
							<If condition={appConfigStore.freightNotifyDetailsFirstNameEnabled}>
								<TextInput
									id="freight-notify-first-name"
									label={intl.formatMessage({
										id: 'freight-booking-wizard__contact-tab__notify-details__first-name__label',
										defaultMessage: 'First name',
									})}
									placeholder={intl.formatMessage({
										id: 'freight-booking-wizard__contact-tab__notify-details__first-name__placeholder',
										defaultMessage: 'Enter first name',
									})}
									required={appConfigStore.freightNotifyDetailsFirstNameRequired}
									value={inner.notifyFirstName ?? ''}
									error={typedErrors?.notifyFirstName}
									onChange={e => updateField('notifyFirstName', e.target.value)}
								/>
							</If>
							<If condition={appConfigStore.freightNotifyDetailsLastNameEnabled}>
								<TextInput
									id="freight-notify-last-name"
									label={intl.formatMessage({
										id: 'freight-booking-wizard__contact-tab__notify-details__last-name__label',
										defaultMessage: 'Last name',
									})}
									placeholder={intl.formatMessage({
										id: 'freight-booking-wizard__contact-tab__notify-details__last-name__placeholder',
										defaultMessage: 'Enter last name',
									})}
									required={appConfigStore.freightNotifyDetailsLastNameRequired}
									value={inner.notifyLastName ?? ''}
									error={typedErrors?.notifyLastName}
									onChange={e => updateField('notifyLastName', e.target.value)}
								/>
							</If>
							<If condition={appConfigStore.freightNotifyDetailsPhoneEnabled}>
								<PhoneInput
									id="freight-notify-phone"
									label={intl.formatMessage({
										id: 'freight-booking-wizard__contact-tab__notify-details__phone__label',
										defaultMessage: 'Phone',
									})}
									placeholder={intl.formatMessage({
										id: 'freight-booking-wizard__contact-tab__notify-details__phone__placeholder',
										defaultMessage: 'Enter phone',
									})}
									required={appConfigStore.freightNotifyDetailsPhoneRequired}
									value={inner.notifyPhone ?? ''}
									error={typedErrors?.notifyPhone}
									onChange={value => updateField('notifyPhone', value)}
								/>
							</If>
							<If condition={appConfigStore.freightNotifyDetailsEmailEnabled}>
								<TextInput
									id="freight-notify-email"
									label={intl.formatMessage({
										id: 'freight-booking-wizard__contact-tab__notify-details__email__label',
										defaultMessage: 'Email',
									})}
									placeholder={intl.formatMessage({
										id: 'freight-booking-wizard__contact-tab__notify-details__email__placeholder',
										defaultMessage: 'Enter email',
									})}
									required={appConfigStore.freightNotifyDetailsEmailRequired}
									value={inner.notifyEmail ?? ''}
									error={typedErrors?.notifyEmail}
									onChange={e => updateField('notifyEmail', e.target.value)}
								/>
							</If>
							<If condition={appConfigStore.freightNotifyDetailsCompanyNameEnabled}>
								<TextInput
									id="freight-notify-company-name"
									label={intl.formatMessage({
										id: 'freight-booking-wizard__contact-tab__notify-details__company-name__label',
										defaultMessage: 'Company name',
									})}
									placeholder={intl.formatMessage({
										id: 'freight-booking-wizard__contact-tab__notify-details__company-name__placeholder',
										defaultMessage: 'Enter company name',
									})}
									required={appConfigStore.freightNotifyDetailsCompanyNameRequired}
									value={inner.notifyCompanyName ?? ''}
									error={typedErrors?.notifyCompanyName}
									onChange={e => updateField('notifyCompanyName', e.target.value)}
								/>
							</If>
						</div>
					</section>
				</If>

				<DynamicFieldsSection
					bookingType="FREIGHT"
					subjectUserDisplayNameId={subjectUserDisplayNameId}
					values={inner.dynamicFieldValues ?? {}}
					errors={(typedErrors as any)?.dynamicFieldValues as Record<string, string> | undefined}
					onChange={next => updateField('dynamicFieldValues', next)}
				/>
			</div>
		</div>
	);
}

export default observer(FreightContactTab);
